import { SITE_STATUS_LABELS, SiteStatus } from '@pubstack/common/src/site'
import { FunctionComponent } from 'react'
import { Colors } from '~/assets/style/colors'
import { AbsentValue } from '~/components/_styled/table'
import { Icon, IconName } from '~/components/Icon'
import { Tooltip } from '~/components/Tooltip'

const SITE_STATUS_ICON: Record<Exclude<SiteStatus, 'inactive'>, { icon: IconName; color: string }> = {
  'to-configure': { icon: 'warning', color: Colors.Warning },
  'out-of-sync': { icon: 'sync_problem', color: Colors.Warning },
  'to-deploy': { icon: 'send', color: Colors.Success },
  'up-to-date': { icon: 'check_circled', color: Colors.Success },
}

export const NOT_ACTIVE_TOOLTIP = 'Nothing is running on this media yet.'

/**
 * A media's status, as the media list's Status column draws it.
 *
 * Two of the five values draw nothing. `'none'` is a media with no status to show, and `inactive` is a media
 * nothing has been set up on yet. Both get the muted dash every other column already uses for an absent
 * value; only the second says why, because only the second is something the customer can act on.
 *
 * `inactive` used to read "Not active" beside a cross, which said something went wrong about the ordinary
 * state of a scope that has just added its medias. The label itself stays in `SITE_STATUS_LABELS`: the
 * status filter still offers it by name, and the CSV export, which has nowhere to hang a tooltip, still
 * writes it out.
 */
export const SiteStatusIndicator: FunctionComponent<{ status: SiteStatus | 'none' }> = ({ status }) => {
  if (status === 'none') {
    return <AbsentValue>—</AbsentValue>
  }
  if (status === 'inactive') {
    return (
      <Tooltip title={NOT_ACTIVE_TOOLTIP}>
        <AbsentValue>—</AbsentValue>
      </Tooltip>
    )
  }
  const { icon, color } = SITE_STATUS_ICON[status]
  return (
    <div className={'flex items-center'}>
      <Icon className={'mr-1'} fill={color} name={icon} height="18px" width="18px" />
      <span>{SITE_STATUS_LABELS[status]}</span>
    </div>
  )
}
